import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Globe, History, Loader2, RotateCcw, Save, X } from 'lucide-react';
import { BackButton } from '../../../common/BackButton';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import sharedReportsAdminService, {
  SharedReport,
  SharedReportHistoryEntry,
} from '../../../../services/sharedReportsAdminService';
import { ReportTypeLabels, ReportType } from '../../../../services/reportConfigurationService';
import type { LabelLayout } from '../../../../types/labelDesign';
import { LabelDesigner } from '../administration/label-designer/LabelDesigner';

interface SharedReportDesignerProps {
  onBack?: () => void;
}

/**
 * Fellesmaler — redigering av de delte rapportmalene i master (WMS_SharedReports).
 * KUN tilgjengelig i IPB-interne tenants (moduleAccess.sharedReportDesigner) for
 * administratorer. Endringer treffer ALLE tenants i miljøet umiddelbart ved lagring
 * (SharedReportCache invalideres av backend). Hver lagring revisjonslogges i
 * WMS_SharedReports_History — «Gjenopprett» i historikk-panelet ruller tilbake.
 */
export const SharedReportDesigner: React.FC<SharedReportDesignerProps> = ({ onBack }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const companyNo = user?.companyNo || '';

  const [reports, setReports] = useState<SharedReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Editor-tilstand
  const [editing, setEditing] = useState<SharedReport | null>(null);
  const [currentLayout, setCurrentLayout] = useState<LabelLayout | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmSave, setConfirmSave] = useState(false);

  // Historikk-panel
  const [historyFor, setHistoryFor] = useState<SharedReport | null>(null);
  const [history, setHistory] = useState<SharedReportHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadReports = useCallback(async () => {
    if (!companyNo) return;
    setLoading(true);
    setError(null);
    try {
      setReports(await sharedReportsAdminService.getAll(companyNo));
    } catch (e: any) {
      setError(e.message || 'Ukjent feil');
    } finally {
      setLoading(false);
    }
  }, [companyNo]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const openEditor = (report: SharedReport) => {
    try {
      setCurrentLayout(JSON.parse(report.layoutJson));
      setEditing(report);
    } catch {
      setError(t('manager.modules.sharedReportDesigner.invalidLayout', {}, 'Malen har ugyldig LayoutJson og kan ikke åpnes.'));
    }
  };

  const doSave = async () => {
    if (!editing || !currentLayout) return;
    setSaving(true);
    setError(null);
    try {
      await sharedReportsAdminService.update(companyNo, editing.reportID, {
        reportType: editing.reportType,
        reportName: editing.reportName,
        description: editing.description,
        externalPrinterWidth: currentLayout.widthMm ?? editing.externalPrinterWidth,
        externalPrinterLength: currentLayout.heightMm ?? editing.externalPrinterLength,
        externalPrinterLandscape: editing.externalPrinterLandscape,
        layoutJson: JSON.stringify(currentLayout),
      });
      setConfirmSave(false);
      setEditing(null);
      setCurrentLayout(null);
      await loadReports();
    } catch (e: any) {
      setError(e.message || 'Lagring feilet');
      setConfirmSave(false);
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async (report: SharedReport) => {
    setHistoryFor(report);
    setHistoryLoading(true);
    try {
      setHistory(await sharedReportsAdminService.getHistory(companyNo, report.reportID));
    } catch (e: any) {
      setError(e.message || 'Kunne ikke hente historikk');
      setHistoryFor(null);
    } finally {
      setHistoryLoading(false);
    }
  };

  const restoreFromHistory = async (entry: SharedReportHistoryEntry) => {
    if (!historyFor) return;
    if (!window.confirm(t('manager.modules.sharedReportDesigner.confirmRestore', {}, 'Gjenopprette denne versjonen? Endringen treffer alle tenants umiddelbart.'))) return;
    setSaving(true);
    setError(null);
    try {
      await sharedReportsAdminService.update(companyNo, historyFor.reportID, {
        reportType: entry.reportType,
        reportName: entry.reportName,
        description: entry.description,
        externalPrinterWidth: entry.externalPrinterWidth,
        externalPrinterLength: entry.externalPrinterLength,
        externalPrinterLandscape: entry.externalPrinterLandscape,
        layoutJson: entry.layoutJson,
      });
      setHistoryFor(null);
      await loadReports();
    } catch (e: any) {
      setError(e.message || 'Gjenoppretting feilet');
    } finally {
      setSaving(false);
    }
  };

  const banner = (
    <div className="bg-orange-100 border-2 border-orange-400 text-orange-900 rounded-lg p-3 mb-4 flex items-center gap-3">
      <AlertTriangle size={22} className="text-orange-600 flex-shrink-0" />
      <div className="text-sm font-semibold">
        {t('manager.modules.sharedReportDesigner.banner', {},
          'Du redigerer FELLESMALER — endringer treffer ALLE tenants i dette miljøet umiddelbart ved lagring.')}
      </div>
    </div>
  );

  // ===== Editor-visning =====
  if (editing && currentLayout) {
    return (
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <button
              onClick={() => { setEditing(null); setCurrentLayout(null); }}
              className="text-gray-500 hover:text-gray-800"
              title={t('common.buttons.cancel', {}, 'Avbryt')}
            >
              <X size={22} />
            </button>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Globe size={20} className="text-orange-600" />
              {editing.reportID} — {editing.reportName}
            </h2>
          </div>
          <button
            onClick={() => setConfirmSave(true)}
            disabled={saving}
            className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
            {t('manager.modules.sharedReportDesigner.saveShared', {}, 'Lagre fellesmal')}
          </button>
        </div>
        {banner}
        {error && <div className="bg-red-50 border border-red-300 text-red-800 rounded p-2 mb-3 text-sm">{error}</div>}

        <LabelDesigner
          initialLayout={currentLayout}
          widthMm={editing.externalPrinterWidth || currentLayout.widthMm || 100}
          heightMm={editing.externalPrinterLength || currentLayout.heightMm || 50}
          reportType={editing.reportType}
          onChange={setCurrentLayout}
        />

        {confirmSave && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md shadow-xl">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle size={22} className="text-orange-600" />
                <h3 className="font-bold text-lg">
                  {t('manager.modules.sharedReportDesigner.confirmTitle', {}, 'Lagre fellesmal?')}
                </h3>
              </div>
              <p className="text-sm text-gray-700 mb-4">
                {t('manager.modules.sharedReportDesigner.confirmBody', {},
                  'Endringen skrives til master og treffer ALLE tenants i miljøet umiddelbart. Forrige versjon lagres i historikken.')}
              </p>
              <div className="flex justify-end gap-2">
                <button onClick={() => setConfirmSave(false)} className="px-4 py-2 rounded border">
                  {t('common.buttons.cancel', {}, 'Avbryt')}
                </button>
                <button onClick={doSave} disabled={saving}
                  className="px-4 py-2 rounded bg-orange-600 text-white disabled:opacity-50">
                  {t('common.buttons.save', {}, 'Lagre')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ===== Liste-visning =====
  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          {onBack && <BackButton onClick={onBack} />}
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Globe size={24} className="text-orange-600" />
            {t('manager.modules.sharedReportDesigner.title', {}, 'Rapport-designer (fellesmaler)')}
          </h1>
        </div>
        <button onClick={loadReports} className="text-sm text-blue-600 hover:underline">
          {t('common.buttons.refresh', {}, 'Oppdater')}
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-3">
        {t('manager.modules.sharedReportDesigner.subtitle', {},
          'Designverktøy for de delte rapportmalene i master — endringer treffer alle tenants i miljøet. Aktiv/skriver styres fortsatt per tenant i Rapport-designer.')}
      </p>
      {banner}
      {error && <div className="bg-red-50 border border-red-300 text-red-800 rounded p-2 mb-3 text-sm">{error}</div>}

      {loading ? (
        <div className="flex items-center gap-2 text-gray-500 p-8 justify-center">
          <Loader2 className="animate-spin" size={20} />
          {t('common.status.loading', {}, 'Laster...')}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="p-3">ID</th>
                <th className="p-3">{t('common.fields.name', {}, 'Navn')}</th>
                <th className="p-3">{t('common.fields.type', {}, 'Type')}</th>
                <th className="p-3">{t('manager.modules.sharedReportDesigner.dimensions', {}, 'Dimensjoner')}</th>
                <th className="p-3">{t('manager.modules.sharedReportDesigner.lastChanged', {}, 'Sist endret')}</th>
                <th className="p-3 text-right">{t('common.fields.actions', {}, 'Handlinger')}</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => {
                const typeLabel = ReportTypeLabels[r.reportType as ReportType] || String(r.reportType);
                return (
                  <tr key={r.reportID} className="border-t hover:bg-orange-50/40">
                    <td className="p-3 font-mono">{r.reportID}</td>
                    <td className="p-3">
                      <div className="font-medium">{r.reportName}</div>
                      {r.description && <div className="text-xs text-gray-500">{r.description}</div>}
                    </td>
                    <td className="p-3">{typeLabel}</td>
                    <td className="p-3">
                      {r.externalPrinterWidth && r.externalPrinterLength
                        ? `${r.externalPrinterWidth} × ${r.externalPrinterLength} mm`
                        : '—'}
                    </td>
                    <td className="p-3 text-gray-500">
                      {r.lastUpdate ? new Date(r.lastUpdate).toLocaleString('nb-NO') : '—'}
                      {r.updatedBy ? ` (${r.updatedBy})` : ''}
                    </td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => openHistory(r)}
                        className="text-gray-500 hover:text-gray-800 mr-3 inline-flex items-center gap-1"
                        title={t('manager.modules.sharedReportDesigner.history', {}, 'Historikk')}
                      >
                        <History size={16} />
                      </button>
                      <button
                        onClick={() => openEditor(r)}
                        className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-1.5 rounded inline-flex items-center gap-1"
                      >
                        {t('manager.modules.sharedReportDesigner.editShared', {}, 'Rediger fellesmal')}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {reports.length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-gray-500">
                  {t('manager.modules.sharedReportDesigner.empty', {}, 'Ingen fellesmaler funnet.')}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Historikk-panel */}
      {historyFor && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-5 max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-xl">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <History size={20} />
                {t('manager.modules.sharedReportDesigner.historyFor', { '0': String(historyFor.reportID) }, 'Historikk for {0}')} — {historyFor.reportName}
              </h3>
              <button onClick={() => setHistoryFor(null)} className="text-gray-500 hover:text-gray-800"><X size={20} /></button>
            </div>
            {historyLoading ? (
              <div className="flex items-center gap-2 text-gray-500 p-6 justify-center">
                <Loader2 className="animate-spin" size={18} />
              </div>
            ) : history.length === 0 ? (
              <p className="text-sm text-gray-500 p-4">
                {t('manager.modules.sharedReportDesigner.noHistory', {}, 'Ingen endringer logget ennå.')}
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-gray-600">
                  <tr>
                    <th className="p-2">{t('common.fields.date', {}, 'Dato')}</th>
                    <th className="p-2">{t('common.fields.type', {}, 'Type')}</th>
                    <th className="p-2">{t('common.fields.user', {}, 'Bruker')}</th>
                    <th className="p-2 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id} className="border-t">
                      <td className="p-2">{new Date(h.changed).toLocaleString('nb-NO')}</td>
                      <td className="p-2">{h.changeType}</td>
                      <td className="p-2">{h.changedBy || '—'}</td>
                      <td className="p-2 text-right">
                        <button
                          onClick={() => restoreFromHistory(h)}
                          disabled={saving}
                          className="text-orange-700 hover:text-orange-900 inline-flex items-center gap-1 disabled:opacity-50"
                          title={t('manager.modules.sharedReportDesigner.restore', {}, 'Gjenopprett denne versjonen')}
                        >
                          <RotateCcw size={15} />
                          {t('manager.modules.sharedReportDesigner.restore', {}, 'Gjenopprett')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SharedReportDesigner;
